import { TFunction } from 'i18next'
import { useMemo } from 'react'

import { PreSaleType } from '@/clients/utoniq-core/schema'
import { DateTime } from '@/core/entities'

export type EventCategoryTagProps = {
  color: string
  bgcolor: string
  text: string
}

/**
 * イベントの状態（開催前、開催中、終了、先行販売の状態など）を判定するフック
 * 表示用のスタイル情報（色、背景色、テキスト）を返す
 */
export const useEventCategoryTag = (
  event: any,
  t: TFunction
): EventCategoryTagProps => {
  return useMemo(() => {
    const havePreSales = !!event.preSales?.edges?.length

    if (havePreSales) {
      //先行販売(先着・抽選)が期間前 → 受付前
      const upcomingPreSale = event.preSales?.edges.every(({ node }: any) => {
        if (node.type === PreSaleType.Lottery) {
          return DateTime.isGt(node.lottery?.applicationStartedAt)
        }
        return DateTime.isGt(node.firstComeFirstServed?.purchasableStartedAt)
      })
      if (upcomingPreSale) {
        return {
          color: 'text.primary',
          bgcolor: 'info.light',
          text: t('upcomingPreSale', '受付前'),
        }
      }

      //先行販売(先着・抽選)が期間内 → 受付中
      const ongoingPreSale = event.preSales?.edges?.some(({ node }: any) => {
        if (node.type === PreSaleType.Lottery) {
          return DateTime.isBetween(
            node.lottery?.applicationStartedAt,
            node.lottery?.applicationEndedAt
          )
        }
        return DateTime.isBetween(
          node.firstComeFirstServed?.purchasableStartedAt,
          node.firstComeFirstServed?.purchasableEndedAt
        )
      })
      if (ongoingPreSale) {
        return {
          color: 'text.primary',
          bgcolor: 'info.light',
          text: t('ongoingPreSale', '受付中'),
        }
      }
    }
    //先行販売期間後

    // event not set endedAt & startedAt → 開催日設定なし
    if (!event.endedAt && !event.startedAt) {
      return {
        color: 'text.secondary',
        bgcolor: 'grey.100',
        text: t('noDateSet', '開催日設定なし'),
      }
    }

    //イベント期間が過去 → 終了
    if (event.endedAt && DateTime.isLt(event.endedAt)) {
      return {
        color: 'text.disabled',
        bgcolor: 'grey.100',
        text: t('end', '終了'),
      }
    }

    //イベント期間が未来 → 開催前
    if (DateTime.isGt(event.startedAt)) {
      return {
        color: 'text.primary',
        bgcolor: 'info.light',
        text: t('upcoming', '開催前'),
      }
    }

    //イベント中 → 開催中
    return {
      color: 'white',
      bgcolor: 'secondary.main',
      text: t('ongoing', '開催中'),
    }
  }, [event.endedAt, event.preSales?.edges, event.startedAt, t])
}
